import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../api";
import { getApiHeaders } from "../../utils/helpers";
import MessageToster from "../MessageToster";

const parseQuizOptions = (item) => {
  if (!item || typeof item !== "object") return [];

  let candidate =
    item.options ??
    item.choices ??
    item.answers ??
    item.option_list ??
    item.options_list ??
    item.question_options;

  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        candidate = JSON.parse(trimmed);
      } catch (e) {
        // continue
      }
    } else if (trimmed.includes(",")) {
      candidate = trimmed.split(",").map((s) => s.trim());
    }
  }

  if (Array.isArray(candidate) && candidate.length > 0) {
    return candidate
      .map((opt, idx) => {
        const defaultLabel = String.fromCharCode(65 + idx);
        if (typeof opt === "string" || typeof opt === "number") {
          return { label: defaultLabel, text: String(opt) };
        }
        if (opt && typeof opt === "object") {
          return {
            label: String(opt.label || opt.key || opt.code || defaultLabel),
            text: String(
              opt.text ??
              opt.option ??
              opt.option_text ??
              opt.title ??
              opt.value ??
              opt.name ??
              opt.ans ??
              opt.answer ??
              ""
            ),
          };
        }
        return { label: defaultLabel, text: String(opt) };
      })
      .filter((o) => Boolean(o.text));
  }

  if (candidate && typeof candidate === "object") {
    const entries = Object.entries(candidate);
    if (entries.length > 0) {
      return entries
        .map(([key, val], idx) => {
          const defaultLabel = isNaN(key) ? key.toUpperCase() : String.fromCharCode(65 + idx);
          const textVal =
            typeof val === "object" && val !== null
              ? val.text ?? val.option ?? val.title ?? val.value ?? ""
              : val;
          return {
            label: defaultLabel,
            text: String(textVal ?? ""),
          };
        })
        .filter((o) => Boolean(o.text));
    }
  }

  const flatGroups = [
    {
      label: "A",
      keys: [
        "option_a",
        "optionA",
        "option1",
        "option_1",
        "opt_a",
        "optA",
        "opt1",
        "opt_1",
        "choice_a",
        "choiceA",
        "choice1",
        "choice_1",
        "ans_a",
        "ansA",
        "ans1",
        "ans_1",
        "answer_a",
        "answerA",
        "answer1",
        "answer_1",
        "a",
        "A",
      ],
    },
    {
      label: "B",
      keys: [
        "option_b",
        "optionB",
        "option2",
        "option_2",
        "opt_b",
        "optB",
        "opt2",
        "opt_2",
        "choice_b",
        "choiceB",
        "choice2",
        "choice_2",
        "ans_b",
        "ansB",
        "ans2",
        "ans_2",
        "answer_b",
        "answerB",
        "answer2",
        "answer_2",
        "b",
        "B",
      ],
    },
    {
      label: "C",
      keys: [
        "option_c",
        "optionC",
        "option3",
        "option_3",
        "opt_c",
        "optC",
        "opt3",
        "opt_3",
        "choice_c",
        "choiceC",
        "choice3",
        "choice_3",
        "ans_c",
        "ansC",
        "ans3",
        "ans_3",
        "answer_c",
        "answerC",
        "answer3",
        "answer_3",
        "c",
        "C",
      ],
    },
    {
      label: "D",
      keys: [
        "option_d",
        "optionD",
        "option4",
        "option_4",
        "opt_d",
        "optD",
        "opt4",
        "opt_4",
        "choice_d",
        "choiceD",
        "choice4",
        "choice_4",
        "ans_d",
        "ansD",
        "ans4",
        "ans_4",
        "answer_d",
        "answerD",
        "answer4",
        "answer_4",
        "d",
        "D",
      ],
    },
    {
      label: "E",
      keys: [
        "option_e",
        "optionE",
        "option5",
        "option_5",
        "opt_e",
        "optE",
        "opt5",
        "opt_5",
        "e",
        "E",
      ],
    },
  ];

  const matched = [];
  for (const group of flatGroups) {
    for (const key of group.keys) {
      if (item[key] != null && item[key] !== "") {
        matched.push({ label: group.label, text: String(item[key]) });
        break;
      }
    }
  }

  if (matched.length > 0) {
    return matched;
  }

  const dynamicKeys = Object.entries(item).filter(
    ([k, v]) =>
      /^(option|opt|choice|ans)/i.test(k) &&
      v != null &&
      v !== "" &&
      typeof v !== "object" &&
      !/id|count|points|point|correct/i.test(k)
  );

  if (dynamicKeys.length > 0) {
    return dynamicKeys.map(([k, v], idx) => ({
      label: String.fromCharCode(65 + idx),
      text: String(v),
    }));
  }

  return [];
};

export default function QuizModal({
  isOpen,
  onClose,
  levelNo: propLevelNo,
  levelName: propLevelName,
  points: propPoints,
  onAnswerSubmitted,
  onSuccess,
}) {
  const [questions, setQuestions] = useState([]);
  const [quizMeta, setQuizMeta] = useState({
    levelNo: null,
    levelName: "",
    points: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [submittedQuestions, setSubmittedQuestions] = useState({});
  const [questionResults, setQuestionResults] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    setCurrentIndex(0);
    setSelectedAnswers({});
    setSubmittedQuestions({});
    setQuestionResults({});
    setIsCompleted(false);
    setIsSubmitting(false);
    setQuestions([]);
    setError(null);
    setLoading(true);

    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/account/learn_earn_todays_questions`, {
          headers: getApiHeaders(),
        });

        const raw = res.data?.data || res.data;

        if (
          (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) &&
          raw
        ) {
          const metaLevelNo =
            raw?.level_no ??
            raw?.current_level_no ??
            raw?.levelNo ??
            (Array.isArray(raw) ? raw[0]?.level_no ?? raw[0]?.current_level_no : null) ??
            propLevelNo ??
            1;

          const metaLevelName =
            raw?.level_name ??
            raw?.current_level_name ??
            raw?.levelName ??
            (Array.isArray(raw) ? raw[0]?.level_name ?? raw[0]?.current_level_name : null) ??
            propLevelName ??
            "Basic";

          const metaPoints =
            raw?.points ??
            raw?.point ??
            raw?.total_points ??
            (Array.isArray(raw) ? raw[0]?.points ?? raw[0]?.point : null) ??
            propPoints ??
            0;

          setQuizMeta({
            levelNo: metaLevelNo,
            levelName: metaLevelName,
            points: metaPoints,
          });

          const rawList = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.questions)
              ? raw.questions
              : Array.isArray(raw?.data)
                ? raw.data
                : Array.isArray(raw?.list)
                  ? raw.list
                  : [];

          if (rawList.length > 0) {
            const preSelected = {};
            const preSubmitted = {};
            const preResults = {};

            const formatted = rawList.map((item, idx) => {
              const opts = parseQuizOptions(item);
              const qId = item.id ?? item.question_id ?? idx + 1;

              // Check if backend returned an existing answer for this question
              const existingAns =
                item.user_answer ??
                item.user_ans ??
                item.user_option ??
                item.selected_option ??
                item.selected_answer ??
                item.remarks ??
                item.submitted_option ??
                item.submitted_answer ??
                item.given_answer ??
                item.given_option ??
                (item.is_answered || item.is_submitted || item.answered
                  ? item.answer ?? item.ans
                  : null);

              const isAnswered =
                Boolean(item.is_answered) ||
                Boolean(item.is_submitted) ||
                Boolean(item.answered) ||
                String(item.status || "").toLowerCase() === "completed" ||
                String(item.status || "").toLowerCase() === "answered" ||
                Boolean(existingAns);

              const rawIsCorrect = item.iscorrect ?? item.is_correct ?? item.isCorrect;
              const hasIsCorrect = rawIsCorrect !== undefined && rawIsCorrect !== null;
              const isCorrectVal = rawIsCorrect === true || rawIsCorrect === "true" || rawIsCorrect === 1;
              const isFalseVal = rawIsCorrect === false || rawIsCorrect === "false" || rawIsCorrect === 0;
              const ptsVal = Number(item.pointsearned ?? item.points_earned ?? (isCorrectVal ? (item.points ?? 10) : 0)) || 0;

              if (existingAns || isAnswered) {
                preSubmitted[qId] = true;
                if (hasIsCorrect) {
                  preResults[qId] = {
                    isCorrect: isCorrectVal ? true : isFalseVal ? false : null,
                    pointsEarned: ptsVal,
                  };
                }
                if (opts && opts.length > 0) {
                  const match =
                    opts.find(
                      (o) =>
                        String(o.label).trim().toUpperCase() ===
                        String(existingAns || "").trim().toUpperCase()
                    ) ||
                    opts.find(
                      (o) =>
                        String(o.text).trim().toLowerCase() ===
                        String(existingAns || "").trim().toLowerCase()
                    ) ||
                    opts[0];
                  if (match) {
                    preSelected[qId] = match;
                  }
                }
              }

              return {
                id: qId,
                level_no: item.level_no ?? metaLevelNo,
                level_name: item.level_name ?? metaLevelName,
                points:
                  item.points ??
                  item.point ??
                  (metaPoints ? Math.round(metaPoints / rawList.length) : 10),
                questionText:
                  item.question ??
                  item.question_text ??
                  item.questionText ??
                  item.title ??
                  `Question ${idx + 1}`,
                options: opts,
              };
            });

            setQuestions(formatted);
            setSelectedAnswers((prev) => ({ ...preSelected, ...prev }));
            setSubmittedQuestions((prev) => ({ ...preSubmitted, ...prev }));
            setQuestionResults((prev) => ({ ...preResults, ...prev }));

            const initialEarned = Object.values(preResults).reduce(
              (acc, r) => acc + (Number(r?.pointsEarned) || 0),
              0
            );
            if (initialEarned > 0) {
              setEarnedPoints(initialEarned);
            }

            const firstUnansweredIdx = formatted.findIndex((q) => !preSubmitted[q.id]);
            if (firstUnansweredIdx > 0) {
              setCurrentIndex(firstUnansweredIdx);
            } else if (firstUnansweredIdx === -1 && formatted.length > 0) {
              setIsCompleted(true);
            }
          } else {
            setQuestions([]);
            setError("No questions available for this level.");
          }
        } else {
          setQuestions([]);
          setError(res.data?.message || res.data?.msg || "Failed to load questions from server.");
        }
      } catch (err) {
        console.error("Error fetching quiz questions:", err);
        const errMsg =
          err.response?.data?.message ||
          err.response?.data?.msg ||
          "Failed to fetch quiz questions from API.";
        setError(errMsg);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [isOpen]);

  if (!isOpen) return null;

  const totalQuestions = questions.length;
  const currentQ = questions[currentIndex] || null;
  const currentLevelNo = currentQ?.level_no ?? quizMeta.levelNo ?? propLevelNo ?? 1;
  const currentLevelName = currentQ?.level_name || quizMeta.levelName || propLevelName || "Basic";
  const currentPoints = currentQ?.points ?? quizMeta.points ?? propPoints ?? 10;
  const selectedOption = currentQ?.id ? selectedAnswers[currentQ.id] : null;
  const isAlreadySubmitted = Boolean(currentQ?.id && submittedQuestions[currentQ.id]);
  const currentResult = currentQ?.id ? questionResults[currentQ.id] : null;

  const handleSelectOption = (opt) => {
    if (!currentQ?.id || isAlreadySubmitted) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQ.id]: opt,
    }));
  };

  const handleNext = async () => {
    if (!currentQ || !selectedOption) return;

    if (isAlreadySubmitted) {
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex((prev) => prev + 1);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const remarksVal = String(selectedOption?.label || selectedOption?.text || "A").trim();

      const payload = {
        id: Number(currentQ.id),
        remarks: remarksVal,
      };

      const res = await axios.post(
        `${BASE_URL}/account/learn_earn_submit_result`,
        payload,
        {
          headers: getApiHeaders(),
        }
      );

      if (
        (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) &&
        res.data?.code !== 400
      ) {
        const rawIsCorrect = res.data?.data?.iscorrect ?? res.data?.data?.is_correct;
        const isCorrect = rawIsCorrect === true || rawIsCorrect === "true" || rawIsCorrect === 1;
        const isFalse = rawIsCorrect === false || rawIsCorrect === "false" || rawIsCorrect === 0;
        const ptsEarned = Number(res.data?.data?.pointsearned ?? res.data?.data?.points_earned ?? (isCorrect ? currentPoints : 0)) || 0;

        // Save question result
        setQuestionResults((prev) => ({
          ...prev,
          [currentQ.id]: {
            isCorrect: isCorrect ? true : isFalse ? false : null,
            pointsEarned: ptsEarned,
          },
        }));

        if (ptsEarned > 0) {
          setEarnedPoints((prev) => prev + ptsEarned);
        }

        // Mark this question as submitted so it cannot be changed when going back
        setSubmittedQuestions((prev) => ({
          ...prev,
          [currentQ.id]: true,
        }));

        const toastMsg = res.data?.message || res.data?.data?.msg;
        if (isCorrect) {
          MessageToster("success", toastMsg || `Correct answer! +${ptsEarned} Pts`);
        } else if (isFalse) {
          MessageToster("error", toastMsg || "Incorrect answer.");
        } else {
          MessageToster("success", toastMsg || "Answer submitted successfully!");
        }

        if (currentIndex < totalQuestions - 1) {
          setCurrentIndex((prev) => prev + 1);
        }

        if (onAnswerSubmitted) {
          onAnswerSubmitted();
        }
      } else {
        MessageToster(
          "error",
          res.data?.message || res.data?.msg || "Failed to submit answer"
        );
      }
    } catch (err) {
      console.error("Error submitting answer:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Something went wrong while submitting your answer.";
      MessageToster("error", errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!currentQ || (!selectedOption && !isAlreadySubmitted)) return;

    if (isAlreadySubmitted) {
      setIsCompleted(true);
      if (onSuccess) {
        onSuccess(earnedPoints);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const remarksVal = String(selectedOption?.label || selectedOption?.text || "A").trim();

      const payload = {
        id: Number(currentQ.id),
        remarks: remarksVal,
      };

      const res = await axios.post(
        `${BASE_URL}/account/learn_earn_submit_result`,
        payload,
        {
          headers: getApiHeaders(),
        }
      );

      if (
        (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) &&
        res.data?.code !== 400
      ) {
        const rawIsCorrect = res.data?.data?.iscorrect ?? res.data?.data?.is_correct;
        const isCorrect = rawIsCorrect === true || rawIsCorrect === "true" || rawIsCorrect === 1;
        const isFalse = rawIsCorrect === false || rawIsCorrect === "false" || rawIsCorrect === 0;
        const ptsEarned = Number(res.data?.data?.pointsearned ?? res.data?.data?.points_earned ?? (isCorrect ? currentPoints : 0)) || 0;

        setQuestionResults((prev) => ({
          ...prev,
          [currentQ.id]: {
            isCorrect: isCorrect ? true : isFalse ? false : null,
            pointsEarned: ptsEarned,
          },
        }));

        setSubmittedQuestions((prev) => ({
          ...prev,
          [currentQ.id]: true,
        }));

        const finalTotal = earnedPoints + ptsEarned;
        setEarnedPoints(finalTotal);
        setIsCompleted(true);

        const toastMsg = res.data?.message || res.data?.data?.msg;
        if (isCorrect) {
          MessageToster("success", toastMsg || `Correct answer! +${ptsEarned} Pts`);
        } else if (isFalse) {
          MessageToster("error", toastMsg || "Incorrect answer.");
        } else {
          MessageToster("success", toastMsg || "Quiz completed successfully!");
        }

        if (onAnswerSubmitted) {
          onAnswerSubmitted();
        }

        if (onSuccess) {
          onSuccess(finalTotal);
        }
      } else {
        MessageToster(
          "error",
          res.data?.message || res.data?.msg || "Failed to submit quiz result"
        );
      }
    } catch (err) {
      console.error("Error submitting quiz:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Something went wrong while submitting your quiz.";
      MessageToster("error", errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{
          backgroundColor: "rgba(15, 23, 42, 0.65)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 1050,
        }}
      />

      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1055 }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isSubmitting) onClose();
        }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "660px" }}>
          <div
            className="modal-content border shadow-lg rounded-4 overflow-hidden"
            style={{
              backgroundColor: "var(--bs-modal-bg, #ffffff)",
              borderColor: "var(--bs-border-color, #e9ecef)",
            }}
          >
            <div
              className="px-4 py-3 d-flex align-items-center justify-content-between border-bottom"
              style={{
                backgroundColor: "var(--bs-modal-bg, #ffffff)",
                borderColor: "var(--bs-border-color, #e9ecef)",
              }}
            >
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                    color: "var(--bs-primary)",
                  }}
                >
                  <i className="bx bx-book-open fs-5"></i>
                </div>
                <h5
                  className="mb-0 fw-bold"
                  style={{ fontSize: "16px", color: "var(--bs-emphasis-color, #343a40)" }}
                >
                  Learn &amp; Earn Quiz
                </h5>

                {!loading && questions.length > 0 && (
                  <div className="d-flex align-items-center gap-2 ms-sm-2 flex-wrap">
                    <span
                      className="badge px-2.5 py-1.5 gap-1 rounded-pill font-size-12 fw-semibold d-inline-flex align-items-center"
                      style={{
                        backgroundColor: "rgba(var(--bs-primary-rgb), 0.1)",
                        color: "var(--bs-primary)",
                        border: "1px solid rgba(var(--bs-primary-rgb), 0.25)",
                      }}
                    >
                      <i className="bx bx-layer me-1 font-size-13"></i> Level {currentLevelNo}: {currentLevelName}
                    </span>

                    <span
                      className="badge px-2.5 gap-1 py-1.5 rounded-pill font-size-12 fw-semibold d-inline-flex align-items-center"
                      style={{
                        backgroundColor: "rgba(var(--bs-warning-rgb), 0.12)",
                        color: "var(--bs-warning)",
                        border: "1px solid rgba(var(--bs-warning-rgb), 0.3)",
                      }}
                    >
                      <i className="bx bxs-star me-1 font-size-13"></i>
                      +{currentPoints} Pts
                    </span>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="btn-close shadow-none"
                onClick={onClose}
                disabled={isSubmitting}
                aria-label="Close"
              />
            </div>

            {loading ? (
              /* Loading State */
              <div
                className="modal-body p-5 text-center"
                style={{ backgroundColor: "var(--bs-modal-bg, #ffffff)" }}
              >
                <div
                  className="spinner-border mb-3"
                  style={{ color: "var(--bs-primary)", width: "2.5rem", height: "2.5rem" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted font-size-14 mb-0">Loading questions from server...</p>
              </div>
            ) : questions.length === 0 ? (
              /* No Questions / Error State */
              <div
                className="modal-body p-5 text-center"
                style={{ backgroundColor: "var(--bs-modal-bg, #ffffff)" }}
              >
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{
                    width: "60px",
                    height: "60px",
                    backgroundColor: "rgba(var(--bs-warning-rgb), 0.15)",
                    color: "var(--bs-warning)",
                  }}
                >
                  <i className="bx bx-info-circle"></i>
                </div>
                <h5 className="fw-semibold mb-2" style={{ color: "var(--bs-emphasis-color, #343a40)" }}>
                  No Questions Found
                </h5>
                <p className="text-muted font-size-14 mb-4">
                  {error || "There are no questions currently configured for this quiz."}
                </p>
                <button
                  type="button"
                  className="btn btn-primary px-4 py-2 font-size-13 rounded-3"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            ) : !isCompleted ? (
              <>
                <div
                  className="modal-body p-4"
                  style={{
                    backgroundColor: "var(--bs-modal-bg, #ffffff)",
                    maxHeight: "70vh",
                    overflowY: "auto",
                  }}
                >
                  <div
                    className="p-3 rounded-3 mb-3 border"
                    style={{
                      backgroundColor: "rgba(var(--bs-primary-rgb), 0.04)",
                      borderColor: "rgba(var(--bs-primary-rgb), 0.15)",
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <h5
                        className="mb-0 fw-semibold lh-base"
                        style={{
                          fontSize: "15px",
                          color: "var(--bs-emphasis-color, #343a40)",
                        }}
                      >
                        {currentQ?.questionText}
                      </h5>
                      <span
                        className="font-size-12 fw-semibold"
                        style={{ color: "var(--bs-primary)", whiteSpace: "nowrap" }}
                      >
                        +{currentPoints} Points
                      </span>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2.5">
                    {currentQ?.options?.map((opt) => {
                      const isSelected = selectedOption?.label === opt.label;

                      let borderColor = "var(--bs-border-color, #e9ecef)";
                      let bgColor = "var(--bs-modal-bg, #ffffff)";
                      let textColor = "var(--bs-emphasis-color, #343a40)";
                      let badgeBg = "var(--bs-tertiary-bg, #f1f3f7)";
                      let badgeColor = "var(--bs-emphasis-color, #495057)";
                      let badgeBorder = "var(--bs-border-color, #e9ecef)";
                      let icon = null;

                      if (isSelected) {
                        if (isAlreadySubmitted && currentResult?.isCorrect === true) {
                          borderColor = "var(--bs-success, #28a745)";
                          bgColor = "rgba(var(--bs-success-rgb, 40, 167, 69), 0.08)";
                          textColor = "var(--bs-success, #28a745)";
                          badgeBg = "var(--bs-success, #28a745)";
                          badgeColor = "#ffffff";
                          badgeBorder = "var(--bs-success, #28a745)";
                          icon = <i className="bx bxs-check-circle font-size-22 text-success"></i>;
                        } else if (isAlreadySubmitted && currentResult?.isCorrect === false) {
                          borderColor = "var(--bs-danger, #dc3545)";
                          bgColor = "rgba(var(--bs-danger-rgb, 220, 53, 69), 0.08)";
                          textColor = "var(--bs-danger, #dc3545)";
                          badgeBg = "var(--bs-danger, #dc3545)";
                          badgeColor = "#ffffff";
                          badgeBorder = "var(--bs-danger, #dc3545)";
                          icon = <i className="bx bxs-x-circle font-size-22 text-danger"></i>;
                        } else {
                          borderColor = "var(--bs-primary)";
                          bgColor = "rgba(var(--bs-primary-rgb), 0.08)";
                          textColor = "var(--bs-primary)";
                          badgeBg = "var(--bs-primary)";
                          badgeColor = "#ffffff";
                          badgeBorder = "var(--bs-primary)";
                          icon = (
                            <i
                              className="bx bxs-check-circle font-size-22"
                              style={{ color: "var(--bs-primary)" }}
                            ></i>
                          );
                        }
                      } else {
                        icon = (
                          <div
                            className="rounded-circle border"
                            style={{
                              width: "20px",
                              height: "20px",
                              borderColor: "var(--bs-border-color, #ced4da)",
                              backgroundColor: "transparent",
                            }}
                          />
                        );
                      }

                      return (
                        <div
                          key={opt.label}
                          onClick={!isAlreadySubmitted ? () => handleSelectOption(opt) : undefined}
                          className="d-flex align-items-center justify-content-between px-3 mb-2 rounded-4 border user-select-none"
                          style={{
                            height: "60px",
                            backgroundColor: bgColor,
                            borderColor: borderColor,
                            boxShadow: isSelected
                              ? `0 0 0 1px ${borderColor}, 0 3px 10px rgba(0,0,0,0.06)`
                              : "0 1px 2px rgba(0,0,0,0.03)",
                            cursor: isAlreadySubmitted ? "default" : "pointer",
                            opacity: isAlreadySubmitted && !isSelected ? 0.55 : 1,
                            pointerEvents: isAlreadySubmitted ? "none" : "auto",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <span
                              className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold font-size-13"
                              style={{
                                width: "32px",
                                height: "32px",
                                backgroundColor: badgeBg,
                                color: badgeColor,
                                border: `1px solid ${badgeBorder}`,
                                transition: "all 0.2s ease",
                              }}
                            >
                              {opt.label}
                            </span>
                            <span
                              className="font-size-14 fw-medium"
                              style={{ color: textColor }}
                            >
                              {opt.text}
                            </span>
                          </div>

                          <div className="flex-shrink-0 ms-2">
                            {icon}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div
                  className="modal-footer px-4 py-3 d-flex align-items-center justify-content-end border-top"
                  style={{
                    backgroundColor: "var(--bs-modal-bg, #ffffff)",
                    borderColor: "var(--bs-border-color, #e9ecef)",
                  }}
                >
                  {currentIndex < totalQuestions - 1 ? (
                    <button
                      key={`quiz-next-btn-${currentIndex}-${isSubmitting ? "submitting" : "ready"}`}
                      type="button"
                      translate="no"
                      className="btn btn-primary d-flex align-items-center font-size-13 fw-semibold px-4 py-2 rounded-3 shadow-sm notranslate"
                      onClick={handleNext}
                      disabled={(!selectedOption && !isAlreadySubmitted) || isSubmitting}
                    >
                      {isSubmitting && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                      <span translate="no" className="notranslate">
                        {isSubmitting ? "Submitting..." : "Next"}
                      </span>
                      {!isSubmitting && (
                        <i className="bx bx-chevron-right font-size-16 ms-1"></i>
                      )}
                    </button>
                  ) : (
                    <button
                      key={`quiz-submit-btn-${currentIndex}-${isSubmitting ? "submitting" : "ready"}`}
                      type="button"
                      translate="no"
                      className="btn btn-primary d-flex align-items-center font-size-13 fw-semibold px-4 py-2 rounded-3 shadow-sm notranslate"
                      onClick={handleSubmitQuiz}
                      disabled={(!selectedOption && !isAlreadySubmitted) || isSubmitting}
                    >
                      {isSubmitting ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="bx bx-check-double font-size-16 me-1"></i>
                      )}
                      <span translate="no" className="notranslate">
                        {isSubmitting ? "Submitting..." : (isAlreadySubmitted ? "Finish" : "Submit Quiz")}
                      </span>
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* Celebration / Result View */
              <div
                className="modal-body p-4 p-sm-5 text-center"
                style={{ backgroundColor: "var(--bs-modal-bg, #ffffff)" }}
              >
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 bg-success-subtle"
                  style={{
                    width: "80px",
                    height: "80px",
                    boxShadow: "0 0 28px rgba(var(--bs-success-rgb), 0.28)",
                  }}
                >
                  <i
                    className="bx bx-trophy font-size-40"
                    style={{ color: "var(--bs-success)" }}
                  ></i>
                </div>

                <h4
                  className="fw-bold mb-2"
                  style={{ color: "var(--bs-emphasis-color, #343a40)" }}
                >
                  Quiz Completed!
                </h4>

                <p className="text-muted font-size-14 mb-4">
                  Congratulations! You have successfully submitted Level {currentLevelNo} ({currentLevelName}) quiz.
                </p>

                <div
                  className="p-3 rounded-4 mb-4 border d-inline-flex align-items-center gap-3 px-4 shadow-sm"
                  style={{
                    backgroundColor: "rgba(var(--bs-warning-rgb), 0.08)",
                    borderColor: "rgba(var(--bs-warning-rgb), 0.25)",
                  }}
                >
                  <i className="bx bxs-star font-size-26 text-warning"></i>
                  <div className="text-start">
                    <div
                      className="font-size-12 fw-bold text-uppercase"
                      style={{ color: "var(--bs-warning)" }}
                    >
                      Reward Unlocked
                    </div>
                    <div
                      className="font-size-16 fw-bold"
                      style={{ color: "var(--bs-emphasis-color, #343a40)" }}
                    >
                      +{earnedPoints} Points Earned
                    </div>
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    className="btn btn-primary px-5 py-2 font-size-14 fw-semibold rounded-3 shadow-sm waves-effect waves-light"
                    onClick={onClose}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
